import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { DutyCode, DutyHourType } from 'generated';
import { omit } from 'lodash';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import getEditDutyCodeSchema from '../utils/editPersonalizedDutyCodeSchema';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import DutyCodeForm from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';
import { LoadingOverlay, showSnackbar } from 'components/shared/index';

import Actions from '../../Actions';
import { StyledDialogMedium } from 'components/shared/Dialog/Dialog.styles';

type Props = {
  isOpen: boolean;
  dutyCode: DutyCode;
  handleClose: () => void;
  onSave: () => void;
  organizationalUnitId?: number;
};

export type DutyCodePeriodForm = {
  id: number;
  dutyHourTypeId: number;
  startTime: Date;
  endTime: Date;
  dutyHourType: DutyHourType;
};

export type DutyCodeForm = InferType<ReturnType<typeof getEditDutyCodeSchema>>;

const EditPersonalizedDutyCode = ({
  isOpen,
  dutyCode,
  handleClose,
  onSave,
  organizationalUnitId
}: Props) => {
  const { t } = useTranslation('common');
  const methods = useForm<DutyCodeForm>({
    resolver: yupResolver(getEditDutyCodeSchema())
  });
  const { handleSubmit, reset } = methods;
  const { mutate: updateDutyCode, isLoading: isUpdating } =
    mutations.useUpdateDutyCode();
  const QueryClient = useQueryClient();

  function handleReset(dutyCode: DutyCode) {
    reset({
      colorCode: dutyCode.colorCode ?? '',
      displayDutyCode: dutyCode.code ?? dutyCode.dutyCode
    });
  }

  useEffect(() => {
    handleReset(dutyCode);
  }, [dutyCode]);

  function onSubmit(data: DutyCodeForm) {
    updateDutyCode(
      {
        ...omit(dutyCode, ['code', 'colorCode']),
        code: data.displayDutyCode,
        colorCode: data.colorCode,
        dutyCodeId: dutyCode.dutyCodeId,
        organizationalUnitId
      },
      {
        onSuccess: () => {
          QueryClient.invalidateQueries(queryKeys.getDutyCodes.name);
          onSave();
        },
        onError: (error: any) => {
          const message = t(
            error?.response?.data?.error?.message || 'somethingWentWrong'
          );
          showSnackbar({
            severity: 'error',
            message: message
          });
        }
      }
    );
  }

  function onDialogClose() {
    handleClose();
  }

  return (
    <StyledDialogMedium
      open={isOpen}
      title={
        dutyCode ? t('editPersonalizedDutyCode') : t('addPersonalizedDutyCode')
      }
      handleClose={onDialogClose}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DutyCodeForm />
          <Actions onDialogClose={onDialogClose} />
          <LoadingOverlay loading={isUpdating} />
        </form>
      </FormProvider>
    </StyledDialogMedium>
  );
};

export default EditPersonalizedDutyCode;
